import React, { useRef, useState } from 'react';
import { Button, Modal, Container, Row, Col, Alert } from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useAppContext,
  useTranslator,
  useToast,
  callNativeAppRemoveLastRoute,
} from '@front-activerse/common-components';
import { Ingredient } from '../types';
import { useBarcodeScanner } from '../hooks';
import Lottie from 'lottie-react';

interface ScanBarCodeContainerProps {
  scanBarcode: (ean: string) => Promise<unknown>;
  cancelScanBarcode: () => void;
}

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
const ADD_INGREDIENT_RETURN_STORAGE_KEY = 'tracker_add_ingredient_return_state';
const ADD_INGREDIENT_RETURN_EVENT = 'tracker_add_ingredient_return';

interface ScanBarCodeLocationState {
  ingredients?: Ingredient[];
  mealName?: string;
  returnPath?: string;
  dietMealId?: string;
  servings?: number;
}

const ScanBarCodeContainer: React.FC<ScanBarCodeContainerProps> = ({ scanBarcode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ScanBarCodeLocationState | null;

  const ingredients = locationState?.ingredients ?? [];
  const mealName = locationState?.mealName ?? '';
  const returnPath = locationState?.returnPath;
  const servings = locationState?.servings;

  const isAnalyzingRef = useRef(false);

  const [noFoodError, setNoFoodError] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [productNotFoundError, setProductNotFoundError] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const multiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');

  const { toastError, toastSuccess } = useToast();
  const { t } = useTranslator();

  const {
    lotties: { loaderStars },
    images: { flashOnIcon, flashOffIcon, infoWhiteIcon, trackerScanBarCodeTipsImg, videoPoster },
  } = useAppContext();

  const handleScanSuccess = async (decodedText: string) => {
    if (isAnalyzingRef.current) return;

    isAnalyzingRef.current = true;
    setNoFoodError(false);
    setRateLimitExceeded(false);
    setProductNotFoundError(false);
    setIsAnalyzing(true);
    try {
      const response: any = await scanBarcode(decodedText);

      if (response.__typename === 'AnalyzedDailyTrackerIngredients') {
        await stop();
        const responseIngredient = response.ingredients[0];

        // User entered amounts for current number of portions
        // We need to divide by multiplier to store for 1 portion in sessionStorage
        const portionMeasure = (responseIngredient.measures ?? []).find(
          (m: any) =>
            m.unit === 'portion' &&
            m.amount?.value > 0 &&
            m.amount?.unit === responseIngredient.baseUnit
        );

        const scale =
          portionMeasure && responseIngredient.baseAmount > 0
            ? portionMeasure.amount.value / responseIngredient.baseAmount
            : 1;

        const baseAmountFor1Portion =
          (portionMeasure ? portionMeasure.amount.value : responseIngredient.baseAmount) /
          multiplier;
        const caloriesFor1Portion = (responseIngredient.calories * scale) / multiplier;
        const proteinFor1Portion = (responseIngredient.proteinGrams * scale) / multiplier;
        const fatFor1Portion = (responseIngredient.fatGrams * scale) / multiplier;
        const carbsFor1Portion = (responseIngredient.carbsGrams * scale) / multiplier;

        const baseMeasurement = {
          amount: baseAmountFor1Portion,
          text: `${baseAmountFor1Portion} ${responseIngredient.baseUnit}`,
          unit: responseIngredient.baseUnit,
        };

        const measurements = portionMeasure
          ? [
              {
                measureId: portionMeasure.measureId,
                amount: 1,
                text: '1 portion',
                unit: 'portion',
              },
              baseMeasurement,
            ]
          : [baseMeasurement];

        const newIngredient: Ingredient = {
          id: responseIngredient.productId || Math.floor(Math.random() * 1000000000),
          name: responseIngredient.name,
          brand: responseIngredient.brand,
          kcal: caloriesFor1Portion,
          measurements,
          macro: {
            protein: { grams: proteinFor1Portion },
            fat: { grams: fatFor1Portion },
            carbohydrates: { grams: carbsFor1Portion },
          },
        };

        toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
        if (returnPath) {
          const payload = {
            returnPath,
            ingredients: [...ingredients, newIngredient],
            mealName,
            ...(servings != null && { servings }),
            updatedAt: Date.now(),
          };
          sessionStorage.setItem(ADD_INGREDIENT_RETURN_STORAGE_KEY, JSON.stringify(payload));
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(ADD_INGREDIENT_RETURN_EVENT, { detail: payload }));
          }
          navigate(-2);
          callNativeAppRemoveLastRoute();
          callNativeAppRemoveLastRoute();
        } else {
          navigate(-2);
        }
      } else if (response.__typename === 'NotFoodError') {
        setNoFoodError(true);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
      } else if (response.__typename === 'ProductNotFoundError') {
        setProductNotFoundError(true);
      } else {
        toastError();
      }
    } catch {
      toastError();
    } finally {
      isAnalyzingRef.current = false;
      setIsAnalyzing(false);
    }
  };

  const handleCloseClick = async () => {
    await stop();
    await new Promise((r) => setTimeout(r, 100));
    navigate(-1);
  };

  const {
    videoRef,
    isCameraActive,
    isScanning,
    isSearchingCamera,
    isTorchSupported,
    isTorchOn,
    error,
    toggleTorch,
    stop,
  } = useBarcodeScanner({
    onDetected: handleScanSuccess,
    onReleaseRequest: handleCloseClick,
  });

  const toggleModal = () => setShowModal(!showModal);

  const handleProductNotFoundClick = () => {
    setProductNotFoundError(false);
    toastSuccess(t('tracker.scanFood.analyze.productReportedConfirmation.content'));
  };

  return (
    <>
      <div className="scan-plate">
        <div className="barcode-reader-container">
          <video ref={videoRef} poster={videoPoster} playsInline muted />
        </div>

        <div
          className={
            isAnalyzing
              ? 'camera-overlay-fullscreen-barcode-lottie'
              : 'camera-overlay-fullscreen-barcode'
          }
        >
          {isCameraActive && !isAnalyzing && <div className="scan-area-barcode" />}

          {isAnalyzing && (
            <div className="scan-area-barcode-lottie">
              <Lottie animationData={loaderStars} loop={true} />
              <p>{t('tracker.describeFood.loader.calculating')}</p>
            </div>
          )}

          <div className="top-controls">
            <div></div>

            <button className="scan-plate-button" onClick={toggleModal}>
              <img src={infoWhiteIcon} alt="" />
            </button>
          </div>

          {!isAnalyzing && (
            <div
              className={
                noFoodError || rateLimitExceeded || productNotFoundError || error
                  ? 'bottom-controls justify-content-center'
                  : 'bottom-controls'
              }
            >
              {noFoodError || rateLimitExceeded || productNotFoundError || error ? (
                <>
                  {noFoodError && (
                    <Alert color="warning" className="mb-0" fade={false}>
                      <p className="mb-0 text-text-main-color text-start">
                        {t('tracker.describeFood.analyze.noFood.notification')}
                      </p>
                    </Alert>
                  )}

                  {rateLimitExceeded && (
                    <Alert color="danger" className="mb-0" fade={false}>
                      <p className="mb-0 text-text-main-color text-start">
                        {t('tracker.limitUsageError.notification')}
                      </p>
                    </Alert>
                  )}

                  {productNotFoundError && (
                    <Alert
                      color="warning"
                      className="mb-0"
                      style={{ marginTop: '-60px' }}
                      fade={false}
                    >
                      <p className="text-text-main-color text-start">
                        {t('tracker.scanFood.analyze.productNotFound.notification')}
                      </p>

                      <div className="text-center">
                        <Button color="link" size="sm" onClick={handleProductNotFoundClick}>
                          <span className="semi-bold">
                            {t('tracker.scanFood.analyze.productNotFound.notification.button')}
                          </span>
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {error && (
                    <div className="text-center">
                      <Alert color="danger" className="mb-0" fade={false}>
                        <p className="mb-0 text-text-main-color text-start">
                          {t('tracker.scanFood.noAccessCamera.notification')}
                        </p>
                      </Alert>
                      {!isCameraActive && !isSearchingCamera && (
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => window.location.reload()}
                          disabled={isSearchingCamera || isScanning}
                        >
                          <p>{t('tracker.scan.button.refreshCamera')}</p>
                        </Button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {isCameraActive && isTorchSupported ? (
                    <button
                      className="scan-plate-button"
                      onClick={toggleTorch}
                      disabled={isScanning}
                    >
                      <img src={isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                    </button>
                  ) : (
                    <button className="scan-plate-button" disabled>
                      <img src={flashOffIcon} alt="" />
                    </button>
                  )}

                  {!isCameraActive && isSearchingCamera && (
                    <p>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t('tracker.scan.findingCamera')}
                    </p>
                  )}

                  {isCameraActive && !isSearchingCamera && (
                    <p>{t('tracker.scan.barcodeDescription')}</p>
                  )}

                  <div style={{ width: '40px', height: '40px' }} />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <img className="img-fluid" src={trackerScanBarCodeTipsImg} alt="" />
                <h2 className="title-medium bold">{t('tracker.tipsModal.title')}</h2>
                <p className="text-text-variant-color mb-xl">
                  {t('tracker.tipsModal.description.barcode')}
                </p>

                <Button color="primary" className="w-100" onClick={toggleModal}>
                  {t('tracker.tipsModal.button')}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

export default ScanBarCodeContainer;
